import type { AxiosResponse } from 'axios';
import {Demographic, SaveDemographicsOutput} from '@/types/respondent';
import { demographicsPostQuery, demographicFilePostQuery } from '@/_queries/demographicsQueries';

/**
 * Add a demographic to the unsaved-demographics map if it is not already present.
 *
 * @param prev - The previous map of unsaved demographics (demographicId → fieldId).
 * @param demographicId - The ID of the demographic.
 * @param fieldId - The ID of the field the demographic belongs to.
 * @returns A new map with the demographic added, or the original map if no change was needed.
 */
export function addUnsavedDemographic(
  prev: Map<string, string>,
  demographicId: string,
  fieldId: string
): Map<string, string> {
  if (prev.has(demographicId)) return prev; // no-op

  const next = new Map(prev);
  next.set(demographicId, fieldId);
  return next;
}

/**
 * Build a demographic object reflecting the respondent’s reply to a field.
 *
 * @param id - The ID of the new demographic.
 * @param fieldId - ID of the field being answered.
 * @param file - When the demographic includes an attached file, the attached file.
 * @param isNumeric - Whether the field values are numbers.
 * @param nowIso - ISO-formatted datetime for when the demographic was created.
 * @param respondentId - ID of the respondent completing the survey.
 * @param selectedIds - List of selected group IDs.
 * @param value - The value of any input replying to the field, or null when no input value was provided.
 * @returns A `Demographic` object.
 */
export function createDemographic(
  id: string,
  fieldId: string,
  file: File | string | null,
  isNumeric: boolean,
  nowIso: string,
  respondentId: string,
  selectedIds: string[],
  value:  string | number | null,
): Demographic {
  const hasValue: boolean = value !== null;

  return {
    id,
    createdAt: nowIso,
    deletedAt: null,
    field: fieldId,
    file: file || null,
    groups: selectedIds,
    respondent: respondentId,
    floatValue: isNumeric && hasValue ? Number(value) : null,
    textValue: !isNumeric && hasValue ? String(value) : '',
  };
}

/**
 * Prepends a new Demographic object as the first/most recent reply to the field in the demographic map. Only add the
 * new demographic if it has different options/values from the previous demographic for the same field.
 *
 * @param demographic - The timestamped demographic to record.
 * @param demographicMap - A map of item IDs to arrays of Demographic objects.
 * @returns A new demographic map with the demographic conditionally appended.
 */
export function prependDemographic(
  demographic: Demographic,
  demographicMap: Map<string, Demographic[]>
): Map<string, Demographic[]> {
  const fieldId = demographic.field;
  const existingDemographics = demographicMap.get(fieldId) ?? [];
  const previousDemographic = existingDemographics[0] ?? null; // Assumes list is sorted most recent to oldest
  const isDemographicIdentical = checkIfDemographicIsIdentical(demographic, previousDemographic);

  if (isDemographicIdentical) {
    return demographicMap; // no change, return same reference
  }

  const next = new Map(demographicMap);
  next.set(fieldId, [demographic, ...existingDemographics]);
  return next;
}

/**
 * Compare two demographics to check if they are identical. Identical means:
 * (1) they are for the same field,
 * (2) they have the same float value, text value, and file,
 * (3) they have the same selected groups in the same order.
 * Differences in demographic IDs and timestamps are ignored.
 *
 * @param newDemographic - The current demographic.
 * @param previousDemographic - The previous demographic for the same field.
 * @returns `true` if the demographics are identical; otherwise `false`.
 */
export function checkIfDemographicIsIdentical(
  newDemographic: Demographic,
  previousDemographic: Demographic | null | undefined
): boolean {
  if (!previousDemographic || previousDemographic.deletedAt) return false;

  const groupsNew = newDemographic.groups ?? [];
  const groupsPrev = previousDemographic.groups ?? [];

  return (
    previousDemographic.floatValue === newDemographic.floatValue &&                 // Same float value
    previousDemographic.textValue === newDemographic.textValue &&                   // Same text value
    previousDemographic.file === newDemographic.file &&                             // Same file
    groupsNew.length === groupsPrev.length &&                                       // Same number of groups
    groupsNew.every((val, idx) => val === groupsPrev[idx])  // Groups are in the same order
  );
}

/**
 * Convert a flat list of Demographic objects into a DemographicMap grouped by field ID.
 *
 * Each key in the resulting map is a field's UUID (demographic.field), and each value is
 * an array of demographics for that field, sorted by descending createdAt timestamp (the most recent demographic first).
 *
 * @param demographics - A list of Demographic objects, potentially unordered.
 * @returns A map where keys are field IDs and values are lists of demographics sorted from most recent to oldest.
 */
export function toDemographicMap(demographics: Demographic[]): Map<string, Demographic[]> {
  const demographicMap = new Map<string, Demographic[]>();

  for (const demographic of demographics) {
    const fieldId = demographic.field;
    if (demographicMap.has(fieldId)) {
      demographicMap.get(fieldId)!.push(demographic);
    } else {
      demographicMap.set(fieldId, [demographic]);
    }
  }

  for (const fieldDemographics of demographicMap.values()) {
    fieldDemographics.sort(
      (a: Demographic, b: Demographic) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  return demographicMap;
}

/**
 * From the unsaved map + demographicMap, build two lists:
 * - withFile: demographics that include an actual File (FormData upload)
 * - withoutFile: demographics to be sent in the batch JSON endpoint
 *
 * @param demographicMap - A map where keys are item IDs and values are arrays of Demographic objects.
 * @param unsaved - A set of stubs, each identifying exactly one unsaved demographic by its `demographicId` and `fieldId`.
 * @returns Two lists: unsaved demographics containing a file, and unsaved demographics without a file.
 */
export function toUnsavedDemographics(
  demographicMap: Map<string, Demographic[]>,
  unsaved: Map<string, string>
): { withFile: Demographic[]; withoutFile: Demographic[], savingIds: Set<string> } {
  if (unsaved.size === 0) return { withFile: [], withoutFile: [], savingIds: new Set<string>() };

  const withFile: Demographic[] = [];
  const withoutFile: Demographic[] = [];

  for (const [demographicId, fieldId] of unsaved) {
    const demographic: Demographic | undefined = demographicMap.get(fieldId)?.find(d => d.id === demographicId);
    if (!demographic) continue;
    (demographic.file instanceof File ? withFile : withoutFile).push(demographic);
  }

  const savingIds = new Set<string>([
    ...withFile.map(d => d.id),
    ...withoutFile.map(d => d.id),
  ]);

  return { withFile, withoutFile, savingIds };
}

/** Save a batch of non-file demographics.
 *
 * @param demographics - The list of demographics to save.
 * @returns The ids of any successfully saved demographics.
 */
export async function saveNonFileDemographics(
  demographics: Demographic[]
): Promise<Set<string>> {
  if (demographics.length === 0) return new Set();
  const response: AxiosResponse<Demographic[]> = await demographicsPostQuery(demographics);
  return new Set(response.data.map(d => d.id));
}

/**
 * Save multiple file demographics in parallel. Returns:
 * - saved: confirmed-saved IDs
 * - errors: array of { id, error } for failed ones (caller can log/set atom)
 */
export async function saveFileDemographics(
  demographics: Demographic[]
): Promise<{ saved: Set<string>; errors: Array<{ id: string; error: Error }> }> {
  const saved = new Set<string>();
  const errors: Array<{ id: string; error: Error }> = [];

  if (demographics.length === 0) return { saved, errors };

  const results = await Promise.allSettled(
    demographics.map(async (d) => {
      const r = await demographicFilePostQuery(d);
      return r.data.id;
    })
  );

  results.forEach((response, idx) => {
    const id = demographics[idx].id;
    if (response.status === 'fulfilled') saved.add(response.value);
    else errors.push({ id, error: (response.reason as Error) ?? new Error('Upload failed') });
  });

  return { saved, errors };
}

/**
 * Save a single iteration of unsaved demographics.
 * - Persists non-file demographics in a single batch request.
 * - Persists file-backed demographics via multipart uploads.
 * - Collects and returns the IDs that were successfully saved.
 * - If file uploads return one or more errors, the first error is returned via `firstFileError`.
 *
 * @param withFile - Demographics that require multipart (file) uploads.
 * @param withoutFile - Demographics that can be saved via batch JSON POST.
 * @returns An object containing a set of demographic IDs that were saved, and if there were any errors uploading a
 *        file, the first error.
 */
export async function saveDemographics(
  withFile: Demographic[],
  withoutFile: Demographic[]
): Promise<SaveDemographicsOutput> {
  const confirmedSaved = new Set<string>();

  if (withoutFile.length > 0) {
    const savedNonFile = await saveNonFileDemographics(withoutFile);
    savedNonFile.forEach(id => confirmedSaved.add(id));
  }

  let firstFileError: Error | null = null;

  if (withFile.length > 0) {
    const { saved, errors } = await saveFileDemographics(withFile);
    saved.forEach(id => confirmedSaved.add(id));
    if (errors.length > 0) firstFileError = errors[0].error;
  }

  return { confirmedSaved, firstFileError };
}